import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../../components/Shell";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Package, ClipboardList, FileDown, Sparkles, ArrowRight } from "lucide-react";

export default function ClinicDashboard() {
    const { user } = useAuth();
    const [promos, setPromos] = useState([]);
    const [orders, setOrders] = useState([]);
    const [productCount, setProductCount] = useState(0);
    const [downloads, setDownloads] = useState([]);

    useEffect(() => {
        api.get("/promotions").then(r => setPromos(r.data)).catch(() => {});
        api.get("/orders").then(r => setOrders(r.data || [])).catch(() => {});
        api.get("/products").then(r => setProductCount((r.data || []).length)).catch(() => {});
        api.get("/downloads").then(r => setDownloads((r.data || []).slice(0, 4))).catch(() => {});
    }, []);

    return (
        <Shell variant="clinic">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 animate-fade-in">
                <div className="flex items-baseline justify-between mb-12">
                    <div>
                        <p className="eyebrow mb-2">Welcome back</p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight">{user?.clinic_name || "Clinic"}</h1>
                    </div>
                    <Link to="/catalogue" data-testid="dash-browse-cta" className="hidden sm:inline-flex btn-primary">Browse catalogue <ArrowRight size={16} strokeWidth={1.5} className="ml-2" /></Link>
                </div>

                {promos.length > 0 && (
                    <div className="card-base p-8 mb-10 bg-gradient-to-r from-zinc-50 to-white" data-testid="promotions-card">
                        <div className="flex items-start gap-4">
                            <Sparkles size={20} strokeWidth={1.5} className="text-black mt-1" />
                            <div className="space-y-4 flex-1">
                                {promos.map((p) => (
                                    <div key={p.id} className="border-b border-zinc-100 pb-4 last:border-0 last:pb-0">
                                        <p className="eyebrow mb-1">Promotion</p>
                                        <h3 className="font-heading text-lg mb-1">{p.title}</h3>
                                        <p className="text-sm text-zinc-600">{p.body}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                    <StatTile icon={<Package size={18} strokeWidth={1.5} />} label="Active products" value={productCount} link="/catalogue" testid="stat-products" />
                    <StatTile icon={<ClipboardList size={18} strokeWidth={1.5} />} label="Your orders" value={orders.length} link="/orders" testid="stat-orders" />
                    <StatTile icon={<FileDown size={18} strokeWidth={1.5} />} label="Resources" value={downloads.length} link="/downloads" testid="stat-downloads" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <div>
                        <div className="flex items-baseline justify-between mb-4">
                            <p className="eyebrow">Recent orders</p>
                            <Link to="/orders" className="text-sm text-zinc-500 hover:text-black">View all →</Link>
                        </div>
                        <div className="card-base divide-y divide-zinc-100" data-testid="recent-orders">
                            {orders.slice(0, 5).map((o) => (
                                <div key={o.id} className="p-4 flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-sm">{o.order_no}</p>
                                        <p className="text-xs text-zinc-500">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} items</p>
                                    </div>
                                    <StatusPill status={o.status} />
                                </div>
                            ))}
                            {orders.length === 0 && <div className="p-6 text-sm text-zinc-500">No orders yet. Browse the catalogue to place your first order.</div>}
                        </div>
                    </div>
                    <div>
                        <div className="flex items-baseline justify-between mb-4">
                            <p className="eyebrow">Latest downloads</p>
                            <Link to="/downloads" className="text-sm text-zinc-500 hover:text-black">View all →</Link>
                        </div>
                        <div className="card-base divide-y divide-zinc-100" data-testid="recent-downloads">
                            {downloads.map((d) => (
                                <div key={d.id} className="p-4 flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-sm">{d.title}</p>
                                        <p className="text-[11px] uppercase tracking-widest text-zinc-400">{d.category}</p>
                                    </div>
                                </div>
                            ))}
                            {downloads.length === 0 && <div className="p-6 text-sm text-zinc-500">Downloads will appear here once the admin adds resources.</div>}
                        </div>
                    </div>
                </div>
            </div>
        </Shell>
    );
}

function StatTile({ icon, label, value, link, testid }) {
    return (
        <Link to={link} data-testid={testid} className="card-base p-6 hover:border-black transition-colors">
            <div className="flex items-center gap-3 mb-4 text-zinc-500">{icon}<span className="eyebrow">{label}</span></div>
            <p className="font-heading text-4xl font-light">{value}</p>
        </Link>
    );
}

export function StatusPill({ status }) {
    const map = {
        pending: "bg-amber-50 text-amber-800 border-amber-200",
        processing: "bg-zinc-100 text-zinc-700 border-zinc-200",
        completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
        cancelled: "bg-red-50 text-red-700 border-red-200",
        approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
        rejected: "bg-red-50 text-red-700 border-red-200",
        suspended: "bg-zinc-100 text-zinc-700 border-zinc-200",
    };
    return <span data-testid={`status-${status}`} className={`inline-flex items-center text-[10px] uppercase tracking-widest px-2.5 py-1 border rounded-sm ${map[status] || "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>{status}</span>;
}
